import { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import axios from "axios";
import { Target, Phone, User, CheckCircle, Loader2, AlertTriangle, Users, Plus, Coins, LogOut } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PENDING_KEY = "darts_pending_join";

export default function JoinEvent() {
  const { eventId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [eventInfo, setEventInfo] = useState(null);
  const [mobile, setMobile] = useState("");
  const [nickname, setNickname] = useState("");
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [joined, setJoined] = useState(false);
  const [joinedName, setJoinedName] = useState("");
  const [joinedTeamName, setJoinedTeamName] = useState("");
  const [left, setLeft] = useState(false);
  const [error, setError] = useState("");
  const [conflict, setConflict] = useState(null);
  const [returningPlayer, setReturningPlayer] = useState(null);
  const [registration, setRegistration] = useState(null); // {registered, nickname, team_name, can_leave}
  const [wallet, setWallet] = useState(null); // {mobile, credits, is_new}

  // Teams-mode "join a team" step
  const [step, setStep] = useState("details"); // details | team-pick | buy-credits | confirming-payment
  const [openTeams, setOpenTeams] = useState([]);
  const [teamsLoading, setTeamsLoading] = useState(false);
  const [newTeamName, setNewTeamName] = useState("");

  // Credits
  const [packages, setPackages] = useState({});
  const [buyingPackage, setBuyingPackage] = useState("");
  const paymentHandledRef = useRef(false);

  const isTeamsMode = eventInfo?.play_mode === "teams";

  const loadOpenTeams = useCallback(async () => {
    setTeamsLoading(true);
    try {
      const r = await axios.get(`${API}/events/${eventId}/teams/open`);
      setOpenTeams(r.data.teams || []);
    } catch {
      setOpenTeams([]);
    }
    setTeamsLoading(false);
  }, [eventId]);

  useEffect(() => {
    axios.get(`${API}/events/${eventId}/join-info`)
      .then(r => { setEventInfo(r.data); setLoading(false); })
      .catch(() => { setError("Event not found or no longer active."); setLoading(false); });
    axios.get(`${API}/credits/packages`).then(r => setPackages(r.data || {})).catch(() => {});
  }, [eventId]);

  const lookupMobile = useCallback(async (num) => {
    const clean = num.replace(/\s+/g, "");
    if (clean.length < 6) { setReturningPlayer(null); setRegistration(null); setWallet(null); return; }
    try {
      const [lookupRes, regRes, walletRes] = await Promise.all([
        axios.get(`${API}/events/${eventId}/lookup-mobile?mobile=${encodeURIComponent(clean)}`),
        axios.get(`${API}/events/${eventId}/my-registration?mobile=${encodeURIComponent(clean)}`),
        axios.get(`${API}/players/wallet?mobile=${encodeURIComponent(clean)}`),
      ]);
      if (lookupRes.data.found) {
        setReturningPlayer(lookupRes.data.nickname);
        setNickname(lookupRes.data.nickname);
      } else {
        setReturningPlayer(null);
      }
      setRegistration(regRes.data.registered ? regRes.data : null);
      setWallet(walletRes.data);
    } catch { /* best-effort */ }
  }, [eventId]);

  useEffect(() => {
    const timer = setTimeout(() => { if (mobile.trim()) lookupMobile(mobile); }, 500);
    return () => clearTimeout(timer);
  }, [mobile, lookupMobile]);

  // --- Returning from PayFast ---
  useEffect(() => {
    const payment = searchParams.get("payment");
    const orderId = searchParams.get("order_id");
    if (!payment || paymentHandledRef.current) return;
    paymentHandledRef.current = true;
    setSearchParams({}, { replace: true });

    const stashed = sessionStorage.getItem(PENDING_KEY);
    const pending = stashed ? JSON.parse(stashed) : null;

    if (payment === "cancelled") {
      setError("Payment was cancelled — no credit was charged. You can try again below.");
      if (pending) { setMobile(pending.mobile || ""); setNickname(pending.nickname || ""); }
      setStep(pending?.type === "create-team" || pending?.type === "join-team" ? "team-pick" : "details");
      if (pending?.type === "create-team" || pending?.type === "join-team") loadOpenTeams();
      return;
    }

    if (payment === "success" && orderId && pending) {
      setStep("confirming-payment");
      setMobile(pending.mobile || "");
      setNickname(pending.nickname || "");
      let attempts = 0;
      const poll = async () => {
        attempts += 1;
        try {
          const r = await axios.get(`${API}/credits/order-status/${orderId}`);
          if (r.data.status === "completed") {
            sessionStorage.removeItem(PENDING_KEY);
            await completePendingAction(pending);
            return;
          }
        } catch { /* keep polling */ }
        if (attempts < 15) {
          setTimeout(poll, 2000);
        } else {
          setError("We're still waiting for payment confirmation. If you were charged, wait a moment and try joining again — your credit will be there.");
          setStep(pending.type === "create-team" || pending.type === "join-team" ? "team-pick" : "details");
        }
      };
      poll();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const completePendingAction = async (pending) => {
    if (pending.type === "join") {
      await doJoin(pending.mobile, pending.nickname);
    } else if (pending.type === "create-team") {
      await doCreateTeam(pending.mobile, pending.nickname, pending.teamName);
    } else if (pending.type === "join-team") {
      await doJoinTeam(pending.mobile, pending.nickname, pending.teamId, pending.teamName);
    }
  };

  const goToBuyCredits = (pending) => {
    sessionStorage.setItem(PENDING_KEY, JSON.stringify(pending));
    setStep("buy-credits");
  };

  const buyPackage = async (pkgKey) => {
    setBuyingPackage(pkgKey);
    setError("");
    try {
      const res = await axios.post(`${API}/credits/create-order`, {
        mobile: mobile.trim(), nickname: nickname.trim(), package: pkgKey,
        return_path: `/join/${eventId}`,
      });
      const { process_url, fields } = res.data;
      const form = document.createElement("form");
      form.method = "POST";
      form.action = process_url;
      Object.entries(fields).forEach(([k, v]) => {
        const input = document.createElement("input");
        input.type = "hidden"; input.name = k; input.value = v;
        form.appendChild(input);
      });
      document.body.appendChild(form);
      form.submit();
    } catch (e) {
      setError(e.response?.data?.detail || "Couldn't start checkout. Please try again.");
      setBuyingPackage("");
    }
  };

  // --- Actual join calls (shared by the direct path and the post-payment path) ---
  const doJoin = async (m, n) => {
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/events/${eventId}/join`, { mobile: m.trim(), nickname: n.trim() });
      setJoined(true);
      setJoinedName(res.data.nickname);
    } catch (e) {
      if (e.response?.status === 402) {
        goToBuyCredits({ type: "join", mobile: m, nickname: n });
        setStep("buy-credits");
      } else if (e.response?.status === 409) {
        try {
          const check = await axios.get(`${API}/events/${eventId}/check-nickname?name=${encodeURIComponent(n.trim())}`);
          setConflict({ name: n.trim(), suggestions: check.data.suggestions });
        } catch { setConflict({ name: n.trim(), suggestions: [] }); }
        setStep("details");
      } else {
        setError(e.response?.data?.detail || "Failed to join. Please try again.");
        setStep("details");
      }
    }
    setSubmitting(false);
  };

  const doCreateTeam = async (m, n, teamName) => {
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/events/${eventId}/teams/self-create`, {
        mobile: m.trim(), nickname: n.trim(), team_name: teamName?.trim() || undefined,
      });
      setJoined(true);
      setJoinedName(n.trim());
      setJoinedTeamName(res.data.team?.team_name || teamName?.trim());
    } catch (e) {
      if (e.response?.status === 402) {
        goToBuyCredits({ type: "create-team", mobile: m, nickname: n, teamName });
        setStep("buy-credits");
      } else if (e.response?.status === 409 && e.response?.data?.detail?.includes("Nickname")) {
        setStep("details");
        setConflict({ name: n.trim(), suggestions: [] });
      } else {
        setError(e.response?.data?.detail || "Couldn't create that team. Try a different name.");
        setStep("team-pick");
      }
    }
    setSubmitting(false);
  };

  const doJoinTeam = async (m, n, teamId, teamNameFallback) => {
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/events/${eventId}/teams/${teamId}/self-join`, {
        mobile: m.trim(), nickname: n.trim(),
      });
      setJoined(true);
      setJoinedName(n.trim());
      setJoinedTeamName(res.data.team?.team_name || teamNameFallback);
    } catch (e) {
      if (e.response?.status === 402) {
        goToBuyCredits({ type: "join-team", mobile: m, nickname: n, teamId, teamName: teamNameFallback });
        setStep("buy-credits");
      } else if (e.response?.status === 409) {
        setStep("details");
        setConflict({ name: n.trim(), suggestions: [] });
      } else {
        setError(e.response?.data?.detail || "Couldn't join that team — it may have filled up.");
        setStep("team-pick");
        loadOpenTeams();
      }
    }
    setSubmitting(false);
  };

  const checkAndSubmit = async () => {
    setError(""); setConflict(null);
    if (isTeamsMode) {
      setSubmitting(true);
      try {
        const check = await axios.get(`${API}/events/${eventId}/check-nickname?name=${encodeURIComponent(nickname.trim())}`);
        if (!check.data.available) {
          setConflict({ name: nickname.trim(), suggestions: check.data.suggestions });
          setSubmitting(false);
          return;
        }
      } catch { /* if the check fails, let the server catch it on submit */ }
      setSubmitting(false);
      setStep("team-pick");
      loadOpenTeams();
      return;
    }
    await doJoin(mobile, nickname);
  };

  const pickSuggestion = (name) => {
    setNickname(name);
    setConflict(null);
  };

  const joinExistingTeam = (team) => doJoinTeam(mobile, nickname, team.id, team.team_name);
  const createNewTeam = () => doCreateTeam(mobile, nickname, newTeamName);

  const leaveEvent = async () => {
    setSubmitting(true);
    setError("");
    try {
      await axios.post(`${API}/events/${eventId}/leave`, { mobile: mobile.trim() });
      setLeft(true);
      setJoined(false);
      setRegistration(null);
    } catch (e) {
      setError(e.response?.data?.detail || "Couldn't remove you from the event. Ask the venue host for help.");
    }
    setSubmitting(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <Loader2 className="h-8 w-8 text-[#E3292E] animate-spin" />
      </div>
    );
  }

  if (!eventInfo || error === "Event not found or no longer active.") {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center p-4">
        <div className="text-center max-w-sm">
          <AlertTriangle className="h-12 w-12 text-yellow-400 mx-auto mb-4" />
          <h1 className="font-heading text-white text-xl mb-2">Event Not Found</h1>
          <p className="text-white/50 text-sm font-body">This event may have ended or the link is invalid.</p>
        </div>
      </div>
    );
  }

  if (step === "confirming-payment") {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center p-4">
        <div className="text-center max-w-sm">
          <Loader2 className="h-8 w-8 text-[#309F6A] animate-spin mx-auto mb-4" />
          <h1 className="font-heading text-white text-xl mb-2">Confirming Payment&hellip;</h1>
          <p className="text-white/50 text-sm font-body">This only takes a few seconds. Don't close this page.</p>
        </div>
      </div>
    );
  }

  if (left) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center p-4">
        <div className="text-center max-w-sm">
          <div className="w-16 h-16 rounded-full bg-white/10 flex items-center justify-center mx-auto mb-4">
            <LogOut className="h-8 w-8 text-white/60" />
          </div>
          <h1 className="font-heading text-white text-2xl mb-2">You're Out</h1>
          <p className="text-white/60 text-sm font-body">You've been removed from {eventInfo.event_name}. Your credit was already spent and isn't refunded.</p>
        </div>
      </div>
    );
  }

  if (joined || (registration?.registered && step === "details" && !conflict)) {
    const displayName = joined ? joinedName : registration.nickname;
    const displayTeam = joined ? joinedTeamName : registration.team_name;
    const canLeave = joined ? (eventInfo.status === "setup" || eventInfo.status === "teams_generated") : registration?.can_leave;
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center p-4">
        <div className="text-center max-w-sm">
          <div className="w-16 h-16 rounded-full bg-[#309F6A]/20 flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="h-8 w-8 text-[#309F6A]" />
          </div>
          <h1 className="font-heading text-white text-2xl mb-2">{joined ? "You're In!" : "You're Already In"}</h1>
          <p className="text-white/60 text-sm font-body mb-6">
            {joined ? "Welcome, " : "Hey "}<span className="text-white font-bold">{displayName}</span>!{" "}
            {isTeamsMode && displayTeam
              ? <>You're on <span className="text-white font-bold">{displayTeam}</span>.</>
              : (joined ? "You've joined the game." : "You're registered for this event.")}
          </p>
          <div className="border border-white/10 bg-[#141414] p-4 mb-6">
            <p className="text-white/40 text-xs uppercase tracking-[0.15em] mb-1">Event</p>
            <p className="text-white font-heading font-bold">{eventInfo.event_name}</p>
            <p className="text-white/40 text-xs mt-2">{eventInfo.venue_name} &middot; {eventInfo.game_format} &middot; {eventInfo.play_mode}</p>
          </div>
          {error && <p className="text-[#E3292E] text-sm font-body mb-4" data-testid="leave-error">{error}</p>}
          {canLeave ? (
            <Button
              onClick={leaveEvent}
              disabled={submitting}
              variant="outline"
              className="w-full border-white/20 text-white/70 hover:bg-white/10 hover:text-white rounded-none font-heading h-11 text-sm"
              data-testid="leave-event-btn"
            >
              {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <LogOut className="h-4 w-4 mr-2" />}
              NOT PLAYING? LEAVE EVENT
            </Button>
          ) : (
            <p className="text-white/30 text-xs font-body">
              {isTeamsMode ? "Hang tight for your first match!" : "The host will assign you to a team. Hang tight!"}
            </p>
          )}
        </div>
      </div>
    );
  }

  if (step === "buy-credits") {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center p-4">
        <div className="w-full max-w-sm">
          <div className="text-center mb-6">
            <Coins className="h-10 w-10 text-[#E3292E] mx-auto mb-3" />
            <h1 className="font-heading text-white text-2xl tracking-wide">GET A CREDIT</h1>
            <p className="text-white/50 text-sm font-body mt-1">1 credit = 1 event. Buy more, save more.</p>
          </div>
          {error && <p className="text-[#E3292E] text-sm font-body mb-4 text-center" data-testid="buy-credits-error">{error}</p>}
          <div className="space-y-2 mb-4">
            {Object.entries(packages).map(([key, pkg]) => (
              <button
                key={key}
                onClick={() => buyPackage(key)}
                disabled={!!buyingPackage}
                className="w-full border border-white/10 bg-[#141414] hover:border-[#E3292E]/40 p-4 flex items-center justify-between text-left transition-colors disabled:opacity-50"
                data-testid={`buy-package-${key}`}
              >
                <div>
                  <p className="text-white font-heading font-bold">{pkg.label}</p>
                  <p className="text-white/40 text-xs font-body mt-0.5">{pkg.desc}</p>
                </div>
                {buyingPackage === key ? (
                  <Loader2 className="h-5 w-5 text-[#E3292E] animate-spin flex-shrink-0" />
                ) : (
                  <span className="text-[#E3292E] font-heading font-bold">R{pkg.price?.toFixed(0)}</span>
                )}
              </button>
            ))}
          </div>
          <p className="text-white/25 text-xs font-body text-center mb-4">You'll be redirected to PayFast to pay securely, then brought right back here.</p>
          <button
            onClick={() => { setStep(isTeamsMode ? "team-pick" : "details"); setError(""); }}
            disabled={!!buyingPackage}
            className="w-full text-white/40 text-xs font-body text-center hover:text-white/60"
          >
            &larr; Back
          </button>
        </div>
      </div>
    );
  }

  if (step === "team-pick" && isTeamsMode) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center p-4">
        <div className="w-full max-w-sm">
          <div className="text-center mb-6">
            <Users className="h-10 w-10 text-[#E3292E] mx-auto mb-3" />
            <h1 className="font-heading text-white text-2xl tracking-wide">PICK YOUR TEAM</h1>
            <p className="text-white/50 text-sm font-body mt-1">Hey {nickname.trim()}, join a team or start a new one</p>
          </div>

          {error && <p className="text-[#E3292E] text-sm font-body mb-4 text-center" data-testid="team-pick-error">{error}</p>}

          {teamsLoading ? (
            <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 text-white/40 animate-spin" /></div>
          ) : (
            <div className="space-y-2 mb-6">
              {openTeams.length === 0 && (
                <p className="text-white/40 text-sm font-body text-center py-4">No teams yet — be the first to start one below.</p>
              )}
              {openTeams.map((team) => (
                <div
                  key={team.id}
                  className="border border-white/10 bg-[#141414] p-3 flex items-center justify-between"
                  data-testid={`open-team-${team.id}`}
                >
                  <div>
                    <p className="text-white font-heading font-bold text-sm">{team.team_name}</p>
                    <p className="text-white/40 text-xs font-body mt-0.5">
                      {team.players.length > 0 ? team.players.join(", ") : "No players yet"}
                    </p>
                  </div>
                  {team.slots_open > 0 ? (
                    <Button
                      size="sm"
                      disabled={submitting}
                      onClick={() => joinExistingTeam(team)}
                      className="bg-[#E3292E] hover:bg-[#DC2626] text-white rounded-none font-heading text-xs flex-shrink-0"
                      data-testid={`join-team-btn-${team.id}`}
                    >
                      JOIN
                    </Button>
                  ) : (
                    <Badge className="rounded-none bg-white/10 text-white/40 text-xs flex-shrink-0">FULL</Badge>
                  )}
                </div>
              ))}
            </div>
          )}

          <div className="border border-white/10 bg-[#141414] p-4 mb-4">
            <label className="text-white/50 text-xs uppercase tracking-[0.15em] font-heading mb-1.5 block">
              <Plus className="h-3 w-3 inline mr-1.5" />Start a New Team
            </label>
            <Input
              placeholder="Team name (optional)"
              value={newTeamName}
              onChange={(e) => setNewTeamName(e.target.value)}
              maxLength={30}
              className="bg-[#0A0A0A] border-white/10 text-white rounded-none font-body h-11 text-base mb-3"
              data-testid="new-team-name-input"
            />
            <Button
              onClick={createNewTeam}
              disabled={submitting}
              className="w-full bg-transparent border border-white/20 hover:bg-white/10 text-white rounded-none font-heading h-11 text-sm"
              data-testid="create-team-btn"
            >
              {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              CREATE NEW TEAM
            </Button>
          </div>

          <button
            onClick={() => setStep("details")}
            className="w-full text-white/40 text-xs font-body text-center hover:text-white/60"
            data-testid="back-to-details-btn"
          >
            &larr; Back
          </button>
        </div>
      </div>
    );
  }

  const cannotJoin = eventInfo.status === "completed" || eventInfo.status === "ended";

  return (
    <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center p-4">
      <div className="w-full max-w-sm">
        {/* Header */}
        <div className="text-center mb-8">
          <Target className="h-10 w-10 text-[#E3292E] mx-auto mb-3" />
          <h1 className="font-heading text-white text-2xl tracking-wide">JOIN GAME</h1>
          <p className="text-white/50 text-sm font-body mt-1">{eventInfo.venue_name}</p>
        </div>

        {/* Event Info */}
        <div className="border border-white/10 bg-[#141414] p-4 mb-6">
          <p className="text-white font-heading font-bold text-lg" data-testid="join-event-name">{eventInfo.event_name}</p>
          <div className="flex gap-2 mt-2">
            <Badge className="rounded-none bg-[#E3292E]/20 text-[#E3292E] text-xs">{eventInfo.game_format}</Badge>
            <Badge className="rounded-none bg-white/10 text-white/60 text-xs">{eventInfo.play_mode}</Badge>
            <Badge className="rounded-none bg-white/10 text-white/60 text-xs">{eventInfo.event_type}</Badge>
          </div>
        </div>

        {cannotJoin ? (
          <div className="text-center">
            <AlertTriangle className="h-8 w-8 text-yellow-400 mx-auto mb-3" />
            <p className="text-white/60 text-sm font-body">This event has ended and is no longer accepting players.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Mobile Number */}
            <div>
              <label className="text-white/50 text-xs uppercase tracking-[0.15em] font-heading mb-1.5 block">
                <Phone className="h-3 w-3 inline mr-1.5" />Mobile Number
              </label>
              <Input
                type="tel"
                placeholder="e.g. 0821234567"
                value={mobile}
                onChange={(e) => { setMobile(e.target.value); setReturningPlayer(null); setRegistration(null); setWallet(null); }}
                className="bg-[#141414] border-white/10 text-white rounded-none font-body h-12 text-base"
                data-testid="join-mobile-input"
              />
              <p className="text-white/25 text-xs font-body mt-1.5">New here? You get 3 free credits to try it out. After that, joining costs 1 credit (R10).</p>
            </div>

            {/* Returning Player Banner */}
            {returningPlayer && (
              <div className="border border-[#309F6A]/30 bg-[#309F6A]/10 p-3 flex items-center gap-2" data-testid="returning-player-banner">
                <CheckCircle className="h-4 w-4 text-[#309F6A] flex-shrink-0" />
                <p className="text-[#309F6A] text-sm font-body">
                  Welcome back, <span className="font-bold text-white">{returningPlayer}</span>!
                </p>
              </div>
            )}

            {/* Wallet Balance / Welcome Bonus Banner */}
            {wallet && wallet.is_new && wallet.credits > 0 && (
              <div className="border border-[#E3292E]/30 bg-[#E3292E]/10 p-3 flex items-center gap-2" data-testid="welcome-bonus-banner">
                <Coins className="h-4 w-4 text-[#E3292E] flex-shrink-0" />
                <p className="text-white text-sm font-body">
                  🎉 Welcome! You've got <span className="font-bold text-[#E3292E]">{wallet.credits} free credits</span> — no need to buy one today.
                </p>
              </div>
            )}
            {wallet && !wallet.is_new && wallet.credits > 0 && (
              <div className="border border-white/10 bg-[#141414] p-3 flex items-center gap-2" data-testid="wallet-balance-banner">
                <Coins className="h-4 w-4 text-[#309F6A] flex-shrink-0" />
                <p className="text-white/70 text-sm font-body">
                  You have <span className="font-bold text-white">{wallet.credits} credit{wallet.credits === 1 ? "" : "s"}</span> ready to use.
                </p>
              </div>
            )}
            {wallet && wallet.credits === 0 && (
              <div className="border border-white/10 bg-[#141414] p-3 flex items-center gap-2" data-testid="wallet-empty-banner">
                <Coins className="h-4 w-4 text-white/30 flex-shrink-0" />
                <p className="text-white/50 text-sm font-body">
                  You're out of credits — we'll take you to buy one after you hit join.
                </p>
              </div>
            )}

            {/* Nickname */}
            <div>
              <label className="text-white/50 text-xs uppercase tracking-[0.15em] font-heading mb-1.5 block">
                <User className="h-3 w-3 inline mr-1.5" />Nickname
              </label>
              <Input
                placeholder="Your display name"
                value={nickname}
                onChange={(e) => { setNickname(e.target.value); setConflict(null); }}
                maxLength={30}
                className="bg-[#141414] border-white/10 text-white rounded-none font-body h-12 text-base"
                data-testid="join-nickname-input"
              />
            </div>

            {/* Nickname Conflict */}
            {conflict && (
              <div className="border border-yellow-500/30 bg-yellow-500/10 p-3" data-testid="nickname-conflict">
                <p className="text-yellow-400 text-sm font-body mb-2">
                  "<span className="font-bold">{conflict.name}</span>" is already taken. Pick one:
                </p>
                <div className="flex flex-wrap gap-2">
                  {conflict.suggestions.map((s) => (
                    <Button
                      key={s}
                      size="sm"
                      variant="outline"
                      className="rounded-none border-yellow-500/30 text-yellow-400 hover:bg-yellow-500/20 text-sm font-body"
                      onClick={() => pickSuggestion(s)}
                      data-testid={`suggestion-${s}`}
                    >
                      {s}
                    </Button>
                  ))}
                </div>
                <p className="text-white/30 text-xs mt-2 font-body">Or type a different nickname above.</p>
              </div>
            )}

            {/* Error */}
            {error && (
              <p className="text-[#E3292E] text-sm font-body" data-testid="join-error">{error}</p>
            )}

            {/* Submit */}
            <Button
              onClick={checkAndSubmit}
              disabled={submitting || !mobile.trim() || !nickname.trim()}
              className="w-full bg-[#E3292E] hover:bg-[#DC2626] text-white rounded-none font-heading h-12 text-base"
              data-testid="join-submit-btn"
            >
              {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              {submitting ? "..." : (isTeamsMode ? "CONTINUE" : "JOIN GAME")}
            </Button>
          </div>
        )}

        {/* Footer */}
        <p className="text-white/20 text-xs text-center mt-8 font-body">Darts Manager</p>
      </div>
    </div>
  );
}
